import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import Button from '../ui/Button';
import CartItem from '../ui/CartItem';

const CartModal = ({ onClose }) => {
  const {
    cartItems,
    clearCart,
    storeGroups,
    subtotal,
    packagingCharge,
    deliveryFee,
    total,
    refreshInventory,
    inventoryLoading,
    stores
  } = useCart();
  const navigate = useNavigate();
  const storeNames = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.name])), [stores]);

  useEffect(() => {
    refreshInventory().catch((err) => console.error('Error refreshing inventory:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Without this, the page underneath stays scrollable while this modal is
  // open - on iOS Safari that makes a `fixed` overlay drift/clip against
  // the real viewport as the background scrolls (reported: cart rendered
  // cut off at the top). Same fix already used by AnnouncementModal.jsx.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const belowMinimumGroups = storeGroups.filter((g) => g.belowMinimum);
  const canCheckout = cartItems.length > 0 && belowMinimumGroups.length === 0;

  const handleCheckout = () => {
    navigate('/checkout');
    onClose();
  };

  const handleContinueShopping = () => {
    navigate('/stores');
    onClose();
  };

  // Build an index into the flat cartItems array for each grouped item, so
  // quantity/remove controls still operate on the right array position.
  const groupsWithIndices = storeGroups.map((group) => ({
    ...group,
    items: group.items.map((item) => ({ item, index: cartItems.indexOf(item) }))
  }));

  // Rendered via a portal straight to document.body, not inline where
  // Header.jsx invokes it - Header's <header> has backdrop-blur-sm, and
  // backdrop-filter (like transform/filter) makes an element the
  // containing block for any `position: fixed` descendant per the CSS
  // spec. Left inline, this modal's "fixed inset-0" was never actually
  // relative to the real viewport - it was relative to that slim header
  // bar, which is why it rendered squeezed into a tiny box with no visible
  // backdrop. A portal makes this modal immune to that (and to any future
  // ancestor that introduces a transform/filter/will-change) regardless of
  // where it's mounted from.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50">
      {/* max-h-full (not a vh value) - it's measured against this box's
          fixed inset-0 flex parent above, which always matches the real
          visible viewport; `vh` units don't on iOS Safari when its
          collapsible toolbar is showing. */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-full flex flex-col">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Your Cart</h2>
          <button onClick={onClose} className="flex items-center justify-center w-11 h-11 -mr-2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close cart">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {inventoryLoading && (
            <div className="text-center py-2 mb-3 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500">Checking item availability...</p>
            </div>
          )}

          {cartItems.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 mb-4">Your cart is empty</p>
              <Button onClick={handleContinueShopping} variant="primary">Browse Stores</Button>
            </div>
          ) : (
            <>
              {groupsWithIndices.length > 1 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
                  Your cart spans {groupsWithIndices.length} stores — this will create {groupsWithIndices.length} separate orders with one combined delivery fee.
                </div>
              )}

              {groupsWithIndices.map((group) => (
                <div key={group.storeId} className="mb-5 last:mb-0">
                  {groupsWithIndices.length > 1 && (
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {storeNames[group.storeId] || 'Store'}
                    </h3>
                  )}
                  {group.belowMinimum && (
                    <p className="text-xs text-red-600 mb-2">
                      Add ₹{(group.minimumOrderValue - group.subtotal).toFixed(2)} more from this store to meet the ₹{group.minimumOrderValue} minimum.
                    </p>
                  )}
                  {group.items.map(({ item, index }) => (
                    <CartItem key={`${item.id}-${index}`} item={item} index={index} />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="p-4 border-t border-gray-100">
            <div className="flex justify-between mb-1.5 text-sm text-gray-600">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {packagingCharge > 0 && (
              <div className="flex justify-between mb-1.5 text-sm text-gray-600">
                <span>Packaging</span>
                <span>₹{packagingCharge.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between mb-1.5 text-sm text-gray-600">
              <span>Delivery Fee</span>
              <span>₹{deliveryFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-4 font-semibold text-gray-900">
              <span>Total</span>
              <span>₹{total.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button onClick={handleCheckout} variant="primary" fullWidth disabled={inventoryLoading || !canCheckout}>
                Checkout
              </Button>
              <Button onClick={onClose} variant="outline" fullWidth>
                Continue Shopping
              </Button>
            </div>

            <button onClick={clearCart} className="mt-3 text-xs text-red-500 hover:text-red-700 text-center w-full transition-colors">
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CartModal;
